/**
 * The audit log — `/v1/admin/audit-events*`.
 *
 * **Cursor-paged, not offset-paged**, and that is not a style choice. An offset page over an
 * append-only log that is still being written shifts under you: rows arrive at the head between
 * two requests, and page 2 re-shows what page 1 already did. A keyset cursor names a position in
 * the log rather than a distance from its start.
 *
 * `verify-chain` is a **mutation, not a query**, because it is work the server does on request and
 * an operator triggers it deliberately. It is also the one thing on this screen that can report
 * bad news about the log itself.
 */
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type AuditEventRow = components['schemas']['AuditEventRow'];
type AuditList = components['schemas']['AuditListResponse'];
type DigestList = components['schemas']['MonthDigestListResponse'];
export type ChainVerdict = components['schemas']['ChainVerifyOut'];

export interface AuditFilter extends Record<string, unknown> {
  category?: string;
  action?: string;
  entity_public_code?: string;
  /** One row's slice, as the History panels deep-link it (doc 03 §6.15). */
  entity_type?: string;
  entity_id?: number;
  limit?: number;
}

export function auditQueryString(filter: AuditFilter, cursor?: number): string {
  const search = new URLSearchParams({ limit: String(filter.limit ?? 50) });
  if (filter.category) search.set('category', filter.category);
  if (filter.action) search.set('action', filter.action);
  if (filter.entity_public_code) search.set('entity_public_code', filter.entity_public_code);
  if (filter.entity_type) search.set('entity_type', filter.entity_type);
  if (filter.entity_id !== undefined) search.set('entity_id', String(filter.entity_id));
  if (cursor !== undefined) search.set('cursor', String(cursor));
  return search.toString();
}

export function useAuditEvents(filter: AuditFilter) {
  return useInfiniteQuery({
    queryKey: keys.audit.list(filter),
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      authedClient.request<AuditList>(`/v1/admin/audit-events?${auditQueryString(filter, pageParam)}`),
    /* `null` means the end of the log, and returning `undefined` is how the query says so. */
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** Everything that happened to one row — the slice every detail drawer embeds. Newest first. */
export function useEntityHistory(entityType: string, entityId: number) {
  return useQuery({
    queryKey: keys.audit.entity(entityType, String(entityId)),
    queryFn: () =>
      authedClient.request<AuditList>(
        `/v1/admin/audit-events/entity/${entityType}/${entityId}?limit=50`,
      ),
  });
}

export function useAuditDigests() {
  return useQuery({
    queryKey: keys.audit.digests(),
    queryFn: () => authedClient.request<DigestList>('/v1/admin/audit-events/digests'),
  });
}

export function useVerifyChain() {
  return useMutation({
    mutationFn: () =>
      /* `{}` and not "no body". Every field of `ChainVerifyIn` is optional, but the request body
         itself is `required: true`, so omitting it entirely is a 422 reading "Field required" —
         which sounds like a missing form field and is not one. Verifying the whole log from its
         head is the default, and that is what an empty object asks for. */
      authedClient.request<ChainVerdict>('/v1/admin/audit-events/verify-chain', { method: 'POST', body: {} }),
  });
}
